import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebaseConfig'; // Firestore setup
import moment from 'moment-timezone';
import { getWordsData } from './utils';
import { DAILY_LEARN_HARD_CAP } from '../utils/constants';

// Function to fetch the actual sentence data from the 'sentences' collection one by one
const getSentenceIdsData = async (sentenceIds) => {
  // Create an array of promises to fetch each sentence document in parallel
  const sentencePromises = sentenceIds.map(async (sentenceId) => {
    const sentenceDocRef = doc(firestore, 'sentences', sentenceId);
    const docSnapshot = await getDoc(sentenceDocRef);

    if (docSnapshot.exists()) {
      const sentenceData = { id: docSnapshot.id, ...docSnapshot.data() };
      // Fetch the words associated with the sentence in parallel
      sentenceData.words = await getWordsData(sentenceData.words || []);
      return sentenceData; // Return sentence data if the document exists
    } else {
      return null; // Return null for non-existent sentences
    }
  });

  // Resolve all promises in parallel
  const sentencesData = await Promise.all(sentencePromises);

  // Filter out any null entries (for non-existent sentences)
  return sentencesData.filter((sentence) => sentence !== null);
};

// Main function to fetch learning sentences
// extraCount: if > 0, bypass the daily limit and fetch this many extra sentences
export const fetchLearnSessionItems = async (userId, userData, learnedTodayData, learnedSentenceIds, isSubscribed, extraCount = 0) => {
  try {
    if (!userId || !userData) {
      throw new Error('Missing required parameters: userId and userData');
    }

    const userSettings = userData.settings || {}; // Use settings or empty object

    // --- 1. Check Vacation Mode ---
    if (userSettings.vacation_started_at) {
      return [];
    }

    // --- 2. Determine Sentences to Learn Today ---
    const userTimezone = userSettings.timezone || 'UTC'; // Default to UTC if not set
    const todayDate = moment().tz(userTimezone).format('YYYY-MM-DD');

    const learnedToday = learnedTodayData?.[todayDate] || 0;
    let sentencesToLearnCount;
    if (extraCount > 0) {
      // Extra study: allow up to extraCount more, but never exceed the 30/day hard cap
      const remainingCapacity = Math.max(0, DAILY_LEARN_HARD_CAP - learnedToday);
      sentencesToLearnCount = Math.min(extraCount, remainingCapacity);
      if (sentencesToLearnCount <= 0) {
        return [];
      }
    } else {
      // Normal: respect daily limit. Fall back to the registration default —
      // without it a missing setting yields NaN, and every `length >= NaN`
      // guard below is false, so the loops collect every unlearned sentence.
      const learnPerDay = userSettings.sentences_per_day ?? 5;
      sentencesToLearnCount = learnPerDay - learnedToday;
      if (sentencesToLearnCount <= 0) {
        return [];
      }
    }

    // --- 3. Determine Allowed Levels Based on Settings & Subscription ---
    const preferredLevels = userSettings.levels_to_learn_from || [5, 4, 3, 2, 1]; // User's preference
    let levelsToProcess = isSubscribed ? preferredLevels : [5];
    levelsToProcess.sort((a, b) => b - a);

    if (levelsToProcess.length === 0) {
      console.warn(`User ${userId} has no levels selected for learning.`);
      return [];
    }

    // --- 4. Fetch Sentence IDs Based on Unlocking Strategy ---
    const learnedSentenceIdsSet = new Set(learnedSentenceIds || []);
    const sentencesToLearn = []; // Array to hold the IDs of sentences to fetch

    // Helper to fetch WK complexity map for a level
    const fetchWKDataForLevel = async (level) => {
      const levelDocRef = doc(firestore, 'ids', `sentences_${level}_wk`);
      const levelDoc = await getDoc(levelDocRef);
      if (!levelDoc.exists()) {
        console.warn(`WK sentences ID document for level ${level} not found.`);
        return {};
      }
      return levelDoc.data().data || {};
    };

    const unlockingMode = userSettings.sentence_unlocking;
    const useRandomUnlocking = unlockingMode === 'random' && isSubscribed;
    const usePureWKUnlocking = unlockingMode === 'wanikani' && isSubscribed;

    const wkMin = userSettings.wk_level_min != null ? Number(userSettings.wk_level_min) : null;
    const wkMax = userSettings.wk_level_max != null ? Number(userSettings.wk_level_max) : null;

    const applyWKFilter = ([, complexity]) => {
      const c = Number(complexity);
      if (wkMin != null && c < wkMin) return false;
      if (wkMax != null && c > wkMax) return false;
      return true;
    };

    if (useRandomUnlocking) {
      // RANDOM (Subscribed Users Only)
      const fetchPromises = levelsToProcess.map((level) => fetchWKDataForLevel(level));
      const results = await Promise.all(fetchPromises);
      const merged = Object.assign({}, ...results);
      let allAllowedSentenceIds = Object.entries(merged)
        .filter(applyWKFilter)
        .map(([id]) => id);

      // Shuffle the combined array (Fisher-Yates shuffle)
      for (let i = allAllowedSentenceIds.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allAllowedSentenceIds[i], allAllowedSentenceIds[j]] = [allAllowedSentenceIds[j], allAllowedSentenceIds[i]];
      }

      // Pick unlearned sentences from shuffled list
      for (const sentenceId of allAllowedSentenceIds) {
        if (sentencesToLearn.length >= sentencesToLearnCount) break;
        if (!learnedSentenceIdsSet.has(sentenceId)) {
          sentencesToLearn.push(sentenceId);
        }
      }
    } else if (usePureWKUnlocking) {
      // PURE KANJI LEVEL (Subscribed Users Only) — merge all allowed levels, sort globally by kanji_complexity
      const fetchPromises = levelsToProcess.map((level) => fetchWKDataForLevel(level));
      const results = await Promise.all(fetchPromises);
      const merged = Object.assign({}, ...results);

      const sortedIds = Object.entries(merged)
        .filter(applyWKFilter)
        .sort((a, b) => Number(a[1]) - Number(b[1]))
        .map(([id]) => id);

      for (const sentenceId of sortedIds) {
        if (sentencesToLearn.length >= sentencesToLearnCount) break;
        if (!learnedSentenceIdsSet.has(sentenceId)) {
          sentencesToLearn.push(sentenceId);
        }
      }
    } else {
      // LEVEL + KANJI COMPLEXITY (Default) — iterate JLPT levels high-to-low, within each level sort by kanji_complexity
      for (const level of levelsToProcess) {
        if (sentencesToLearn.length >= sentencesToLearnCount) break;

        const data = await fetchWKDataForLevel(level);
        const levelSentenceIds = Object.entries(data)
          .filter(applyWKFilter)
          .sort((a, b) => Number(a[1]) - Number(b[1]))
          .map(([id]) => id);

        for (const sentenceId of levelSentenceIds) {
          if (sentencesToLearn.length >= sentencesToLearnCount) break;
          if (!learnedSentenceIdsSet.has(sentenceId)) {
            sentencesToLearn.push(sentenceId);
          }
        }
      }
    }

    // --- 5. Fetch Full Sentence Data ---
    if (sentencesToLearn.length === 0) {
      return [];
    }
    const sentencesData = await getSentenceIdsData(sentencesToLearn);
    return sentencesData;
  } catch (error) {
    console.error(`Error fetching learning sentences for userId ${userId}:`, error);
    return []; // Return empty array on error
  }
};
